import { useEffect, useState } from "react";
import type { Qubic, QubicKind, Subscription } from "api";
import cn from "classnames";
import type { LoadStatus } from "hooks/useProfile";
import type { KindTab } from "./KindTabs";
import { KindTabs } from "./KindTabs";
import type { StoreCategory } from "./store.model";
import { ownedUsage, qubicsOfKind, STORE_CATEGORIES, webWidgets } from "./store.model";
import { StoreCard } from "./StoreCard";
import { StoreWidgets } from "./StoreWidgets";
import { WIDGETS } from "./WidgetPreviews";

const KIND_TABS: KindTab[] = [
  { kind: "api", title: "API & MCP Integration", text: "Connect qubics via API to your systems" },
  { kind: "web", title: "Web Integration", text: "Use ready-to-integrate web apps and widgets" },
];

type StorePaneProps = {
  kind: QubicKind;
  qubics: Qubic[];
  status: LoadStatus;
  subscriptions: Subscription[];
  onSelectKind: (kind: QubicKind) => void;
  onManage: () => void;
  /** Called once when the pane opens, so ownership and usage are current. */
  onOpened: () => void;
};

/**
 * Qubics Store: the whole catalogue, split into what plugs in over the API and
 * what embeds as a widget. The kind lives in the URL (it is the pane's sub-tab,
 * so My Qubics can send a visitor straight to either); the category is a
 * passing filter and stays local.
 */
export const StorePane = ({
  kind,
  qubics,
  status,
  subscriptions,
  onSelectKind,
  onManage,
  onOpened,
}: StorePaneProps) => {
  const [category, setCategory] = useState<StoreCategory>("all");

  useEffect(() => onOpened(), [onOpened]);

  const cards = qubicsOfKind(qubics, "api", category);
  const widgets = webWidgets(WIDGETS, qubics, category);
  const empty = kind === "api" ? cards.length === 0 : widgets.length === 0;

  return (
    <div className="pc-pane">
      <div className="nw__hd">
        <div>
          <h1 className="nw__h">Qubics Store</h1>
          <p className="nw__t">Discover and subscribe to Qubictron logistics IT solutions.</p>
        </div>
      </div>

      <KindTabs tabs={KIND_TABS} active={kind} onSelect={onSelectKind} />

      {status === "loading" && <span className="pc-spin" aria-label="Loading" />}

      {status === "failed" && (
        <p className="pc-note pc-note--bad">Could not load the store. Please refresh the page.</p>
      )}

      {status === "unverified" && <p className="pc-note">Verify your email address to browse the Qubics Store.</p>}

      {status === "ready" && (
        <>
          <div className="st-fltr" role="tablist">
            {STORE_CATEGORIES.map((item) => (
              <button
                key={item.key}
                type="button"
                className={cn("st-f", { "is-on": item.key === category })}
                role="tab"
                aria-selected={item.key === category}
                onClick={() => setCategory(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {kind === "api" && cards.length > 0 && (
            <div className="st-grid">
              {cards.map((qubic) => (
                <StoreCard
                  key={qubic.slug}
                  qubic={qubic}
                  usage={qubic.owned ? ownedUsage(qubic, subscriptions) : null}
                  onManage={onManage}
                />
              ))}
            </div>
          )}

          {kind === "web" && widgets.length > 0 && <StoreWidgets items={widgets} />}

          {empty && <div className="st-none">No products in this category yet.</div>}
        </>
      )}
    </div>
  );
};
